import React, { useEffect, useState } from 'react';
import {
  getAccessPolicies,
  createAccessPolicy,
  updateAccessPolicy,
  deleteAccessPolicy
} from '../services/api';
import './AdminPages.css';

const emptyPolicy = {
  name: '',
  description: '',
  isActive: true,
  matchPrivateOnly: false,
  matchOwnerPattern: '',
  requireMultiApproval: false,
  minJustificationLength: 10,
  maxPermission: 'admin',
  defaultExpiryDays: '',
  allowBreakGlass: true
};

const AccessPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [form, setForm] = useState(emptyPolicy);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getAccessPolicies();
      setPolicies(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      ...form,
      defaultExpiryDays: form.defaultExpiryDays === '' ? null : Number(form.defaultExpiryDays)
    };
    try {
      if (editingId) await updateAccessPolicy(editingId, payload);
      else await createAccessPolicy(payload);
      setForm(emptyPolicy);
      setEditingId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    }
  };

  if (loading) return <div className="admin-page"><div className="spinner" /></div>;

  return (
    <div className="admin-page">
      <h1 className="page-title">Access Policies</h1>
      <p className="page-subtitle">Templates that enforce multi-step approval, justification length, max permission, and default expiry.</p>
      {error && <div className="alert alert-error">{error}</div>}

      <form className="card admin-form" onSubmit={handleSubmit}>
        <h3>{editingId ? 'Edit policy' : 'New policy'}</h3>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="form-input" value={form.name} required
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Owner pattern (e.g. acme-*)</label>
            <input className="form-input" value={form.matchOwnerPattern}
              onChange={(e) => setForm({ ...form, matchOwnerPattern: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <input className="form-input" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Min justification length</label>
            <input type="number" className="form-input" min={1} value={form.minJustificationLength}
              onChange={(e) => setForm({ ...form, minJustificationLength: Number(e.target.value) })} />
          </div>
          <div className="form-group">
            <label className="form-label">Max permission</label>
            <select className="form-select" value={form.maxPermission}
              onChange={(e) => setForm({ ...form, maxPermission: e.target.value })}>
              <option value="read">Read</option>
              <option value="write">Write</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Default expiry (days)</label>
            <input type="number" className="form-input" min={1} placeholder="optional"
              value={form.defaultExpiryDays}
              onChange={(e) => setForm({ ...form, defaultExpiryDays: e.target.value })} />
          </div>
        </div>
        <div className="checkbox-row">
          <label><input type="checkbox" checked={form.matchPrivateOnly}
            onChange={(e) => setForm({ ...form, matchPrivateOnly: e.target.checked })} /> Private repos only</label>
          <label><input type="checkbox" checked={form.requireMultiApproval}
            onChange={(e) => setForm({ ...form, requireMultiApproval: e.target.checked })} /> Require multi-step</label>
          <label><input type="checkbox" checked={form.allowBreakGlass}
            onChange={(e) => setForm({ ...form, allowBreakGlass: e.target.checked })} /> Allow break-glass</label>
          <label><input type="checkbox" checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary">{editingId ? 'Update' : 'Create'}</button>
          {editingId && (
            <button type="button" className="btn btn-secondary" onClick={() => { setEditingId(null); setForm(emptyPolicy); }}>Cancel</button>
          )}
        </div>
      </form>

      <div className="card-list">
        {policies.map((p) => (
          <div key={p._id} className="card policy-card">
            <div className="policy-header">
              <h3>{p.name} {!p.isActive && <span className="badge">inactive</span>}</h3>
              <div className="repo-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => {
                  setEditingId(p._id);
                  setForm({
                    name: p.name,
                    description: p.description || '',
                    isActive: p.isActive,
                    matchPrivateOnly: p.matchPrivateOnly,
                    matchOwnerPattern: p.matchOwnerPattern || '',
                    requireMultiApproval: p.requireMultiApproval,
                    minJustificationLength: p.minJustificationLength,
                    maxPermission: p.maxPermission,
                    defaultExpiryDays: p.defaultExpiryDays ?? '',
                    allowBreakGlass: p.allowBreakGlass
                  });
                }}>Edit</button>
                <button className="btn btn-sm btn-danger" onClick={async () => {
                  if (window.confirm('Delete this policy?')) {
                    await deleteAccessPolicy(p._id);
                    load();
                  }
                }}>Delete</button>
              </div>
            </div>
            <p>{p.description || 'No description'}</p>
            <ul className="policy-meta">
              <li>Multi-step: {p.requireMultiApproval ? 'yes' : 'no'}</li>
              <li>Min justification: {p.minJustificationLength}</li>
              <li>Max permission: {p.maxPermission}</li>
              <li>Default expiry: {p.defaultExpiryDays ?? 'none'} days</li>
              {p.matchOwnerPattern && <li>Owner pattern: {p.matchOwnerPattern}</li>}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AccessPolicies;
